import { Component, inject, ChangeDetectionStrategy } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatDividerModule } from '@angular/material/divider'
import { AsyncPipe, CommonModule } from '@angular/common'
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MeetingsService, MeetingTimelineResponse, MeetingTimelineUserActivity } from '../meetings.service'
import { AvatarComponent } from '../avatar/avatar.component'
import { map, shareReplay } from 'rxjs'
import { ATTENDEE_EVENT_TYPES, groupTimelineEvents, TimelineEvent, TimelineGroup } from './timeline-sessions'

export type MeetingTimelineDialogData = { id: number }

@Component({
  selector: 'app-meeting-timeline-dialog',
  standalone: true,
  imports: [
    CommonModule,
    AsyncPipe,
    MatDialogModule,
    MatIconModule,
    MatDividerModule,
    TranslatePipe,
    TranslateDirective,
    MatTooltipModule,
    MatProgressSpinner,
    AvatarComponent,
    MatButton,
    MatIconButton,
  ],
  templateUrl: './meeting-timeline-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meeting-timeline-dialog.component.scss',
})
export class MeetingTimelineDialogComponent {
  // The id alone, so every screen that lists meetings can open this on whatever shape it holds.
  meeting: MeetingTimelineDialogData = inject(MAT_DIALOG_DATA)
  meetingsService = inject(MeetingsService)
  sortAscending = false

  timeline$ = this.meetingsService.getMeetingTimeline(this.meeting.id)

  groups$ = this.timeline$.pipe(
    map((result) => groupTimelineEvents(result.data?.events ?? [])),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  private expandedGroups = new Set<TimelineGroup>()

  toggleSort() {
    this.sortAscending = !this.sortAscending
  }

  sortGroups(groups: TimelineGroup[]): TimelineGroup[] {
    return this.sortAscending ? groups : [...groups].toReversed()
  }

  changeCountKey(group: TimelineGroup): string {
    return group.detailEvents.length === 1 ? 'MEETINGS.TIMELINE.SESSION_CHANGE_COUNT_ONE' : 'MEETINGS.TIMELINE.SESSION_CHANGE_COUNT_OTHER'
  }

  toggleGroup(group: TimelineGroup) {
    const isExpanded = this.expandedGroups.has(group)
    if (isExpanded) {
      this.expandedGroups.delete(group)
      return
    }
    this.expandedGroups.add(group)
  }

  isExpanded(group: TimelineGroup): boolean {
    return this.expandedGroups.has(group)
  }

  detailKey(detail: TimelineEvent): string {
    const key = `MEETINGS.TIMELINE.METRIC_TYPES.${detail.type}`
    return ATTENDEE_EVENT_TYPES.includes(detail.type) && !detail.metadata?.attendeeName ? `${key}_UNNAMED` : key
  }

  fieldChangeKey(fieldChange: { field: string; change?: string }): string {
    if (fieldChange.field === 'BLOCK') {
      return `MEETINGS.TIMELINE.METRIC_TYPES.FIELD_BLOCK_${fieldChange.change}`
    }
    return `MEETINGS.TIMELINE.METRIC_TYPES.FIELD_${fieldChange.field}`
  }

  getEventIcon(type: string): string {
    switch (type) {
      case 'CREATE_MEETING':
        return 'add_circle'
      case 'TOGGLE_ON_MEETING':
        return 'toggle_on'
      case 'TOGGLE_OFF_MEETING':
        return 'toggle_off'
      case 'POSTPONE_MEETING':
        return 'schedule'
      case 'UPLOAD_RESOURCE':
        return 'upload_file'
      case 'DELETE_RESOURCE':
        return 'delete_outline'
      case 'JOIN_MEETING_NOW':
        return 'play_arrow'
      case 'JOIN_MEETING':
        return 'meeting_room'
      case 'PAUSE_RECORDING':
        return 'pause_circle'
      case 'RESUME_RECORDING':
        return 'play_circle'
      case 'HANGUP_MEETING':
        return 'call_end'
      case 'LEAVE_MEETING':
        return 'exit_to_app'
      case 'COMPLETE_MINUTES_GENERATION':
        return 'check_circle'
      case 'VIEW_MEETING':
        return 'visibility'
      case 'DOWNLOAD_VIDEO_RECORDING':
        return 'videocam'
      case 'DOWNLOAD_AUDIO_RECORDING':
        return 'audiotrack'
      case 'DOWNLOAD_TRANSCRIPT':
        return 'description'
      case 'DOWNLOAD_MINUTES':
        return 'summarize'
      case 'DOWNLOAD_OUTLINE':
        return 'list_alt'
      case 'DOWNLOAD_ACTION_ITEMS':
        return 'list_alt'
      case 'REPROCESS_OUTPUTS':
        return 'refresh'
      case 'PROCESS_TEMPLATE_ITEMS':
        return 'checklist_rtl'
      case 'SHARE_MEETING_WITH_EXISTING_USER':
      case 'SHARE_MEETING_WITH_NEW_USER':
        return 'person_add'
      case 'ADJUST_RETENTION_PERIOD':
        return 'event_repeat'
      case 'REQUEST_RETENTION_EXTENSION':
        return 'more_time'
      case 'DECLINE_RETENTION_EXTENSION_REQUEST':
        return 'event_busy'
      case 'TEMPLATE_SESSION':
        return 'edit_note'
      case 'LOAD_TEMPLATE':
        return 'folder_open'
      case 'CREATE_BLANK_TEMPLATE':
        return 'note_add'
      case 'ADD_TEMPLATE_ITEM':
        return 'playlist_add'
      case 'REMOVE_TEMPLATE_ITEM':
        return 'playlist_remove'
      case 'MOVE_TEMPLATE_ITEM':
        return 'swap_vert'
      case 'EDIT_TEMPLATE_ITEM':
        return 'edit'
      case 'ATTACH_TEMPLATE_DOCUMENT':
        return 'attach_file'
      case 'DETACH_TEMPLATE_DOCUMENT':
        return 'link_off'
      case 'ATTENDEE_SESSION':
        return 'groups'
      case 'ADD_ATTENDEE':
        return 'person_add'
      case 'UPDATE_ATTENDEE':
        return 'edit'
      case 'DELETE_ATTENDEE':
        return 'person_remove'
      default:
        return 'info'
    }
  }

  getEventColor(type: string): string {
    switch (type) {
      case 'CREATE_MEETING':
        return 'primary'
      case 'TOGGLE_ON_MEETING':
        return 'success'
      case 'TOGGLE_OFF_MEETING':
        return 'error'
      case 'POSTPONE_MEETING':
        return 'warning'
      case 'UPLOAD_RESOURCE':
        return 'info'
      case 'DELETE_RESOURCE':
        return 'warning'
      case 'JOIN_MEETING_NOW':
        return 'accent'
      case 'JOIN_MEETING':
        return 'success'
      case 'PAUSE_RECORDING':
        return 'warning'
      case 'RESUME_RECORDING':
        return 'success'
      case 'HANGUP_MEETING':
        return 'error'
      case 'LEAVE_MEETING':
        return 'default'
      case 'COMPLETE_MINUTES_GENERATION':
        return 'success'
      case 'VIEW_MEETING':
        return 'info'
      case 'DOWNLOAD_VIDEO_RECORDING':
      case 'DOWNLOAD_AUDIO_RECORDING':
      case 'DOWNLOAD_TRANSCRIPT':
      case 'DOWNLOAD_MINUTES':
      case 'DOWNLOAD_OUTLINE':
      case 'DOWNLOAD_ACTION_ITEMS':
        return 'info'
      case 'REPROCESS_OUTPUTS':
      case 'PROCESS_TEMPLATE_ITEMS':
        return 'accent'
      case 'SHARE_MEETING_WITH_EXISTING_USER':
      case 'SHARE_MEETING_WITH_NEW_USER':
        return 'primary'
      case 'ADJUST_RETENTION_PERIOD':
      case 'REQUEST_RETENTION_EXTENSION':
        return 'warning'
      case 'DECLINE_RETENTION_EXTENSION_REQUEST':
        return 'error'
      case 'TEMPLATE_SESSION':
      case 'ATTENDEE_SESSION':
        return 'info'
      default:
        return 'default'
    }
  }

  getUsersWhoEnabledAssistant(data: MeetingTimelineResponse): MeetingTimelineUserActivity[] {
    return data.userActivities.filter((user) => user.hasEnabledAssistant)
  }

  getUsersWhoViewed(data: MeetingTimelineResponse): MeetingTimelineUserActivity[] {
    return data.userActivities.filter((user) => user.viewCount > 0)
  }
}
